/**
 * src/components/Integrations/LookerDetailModal.jsx
 *
 * A dialog showing details about the Looker integration, plus an "Install" or "Update" button.
 *
 * Updated to:
 *  1) Check Firestore for existing googleAuth tokens under
 *     /organizations/{orgId}/integrations/looker.
 *  2) If present, we say "Update Integration" instead of "Install".
 *  3) The user can still click "Update" to re-auth if tokens have expired,
 *     or to add more forms in the wizard afterwards.
 */

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress
} from "@mui/material";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { auth } from "../../firebase";
import { useOrganization2 } from "../../contexts/OrganizationContext2";

export default function LookerDetailModal({
  open,
  onClose,
  onInstall // renamed usage from "Install" to "Install/Update"
}) {
  // Track any error message
  const [errorMsg, setErrorMsg] = useState("");
  // Track whether user already has Looker linked (googleAuth present)
  const [isInstalled, setIsInstalled] = useState(false);
  // Optional: loading state while checking Firestore
  const [checkingInstallation, setCheckingInstallation] = useState(true);

  const { organization } = useOrganization2();

  useEffect(() => {
    async function checkInstallation() {
      if (!open) return; // Only check when modal is open
      setErrorMsg("");
      setIsInstalled(false);
      setCheckingInstallation(true);

      // Must have an org ID
      if (!organization?.id) {
        setCheckingInstallation(false);
        return;
      }

      // Check Firestore doc
      try {
        const lookerRef = doc(
          db,
          "organizations",
          organization.id,
          "integrations",
          "looker"
        );
        const snap = await getDoc(lookerRef);
        if (snap.exists() && snap.data()?.googleAuth) {
          // We have some googleAuth tokens => consider it installed
          setIsInstalled(true);
        } else {
          setIsInstalled(false);
        }
      } catch (err) {
        console.error("[LookerDetailModal] Error checking installation:", err);
        setErrorMsg("Failed to check Looker installation status. Try again?");
      } finally {
        setCheckingInstallation(false);
      }
    }

    checkInstallation();
  }, [open, organization?.id]);

  /**
   * handleInstallClick:
   *  1) Ensure user is logged in with email/password (auth.currentUser).
   *  2) If no org, show error.
   *  3) Otherwise, redirect to the lookerAuth function's /auth/google?orgId=... route.
   *  4) If anything fails, display the error message in an Alert.
   */
  const handleInstallClick = () => {
    // Clear any old error
    setErrorMsg("");

    if (!auth.currentUser) {
      console.warn("[LookerDetailModal] No currentUser. Must be signed in first.");
      setErrorMsg("Please sign in with your existing account first, then try again.");
      return;
    }

    if (!organization?.id) {
      console.warn("[LookerDetailModal] No organization found. Cannot proceed.");
      setErrorMsg("No organization found. Please contact support or try again.");
      return;
    }

    try {
      // If dev (localhost), use local emulator URL. Otherwise use prod URL.
      const isDev = window.location.hostname.includes("localhost");
      const baseUrl = isDev
        ? "http://localhost:5001/myutms-f8cba/us-central1/lookerAuth/auth/google"
        : "https://us-central1-myutms-f8cba.cloudfunctions.net/lookerAuth/auth/google";

      // Build the full URL with orgId
      const targetUrl = `${baseUrl}?orgId=${organization.id}`;

      console.log(
        `[LookerDetailModal] Redirecting user to OAuth flow. 
         orgId=${organization.id}, URL=${targetUrl}`
      );

      // Perform the redirect to re-auth or first-time auth
      window.location.href = targetUrl;

      // Optionally close the modal
      // onClose();
    } catch (err) {
      console.error("[LookerDetailModal] Redirect failed:", err);
      setErrorMsg(err.message || "Failed to start OAuth flow.");
    }
  };

  // If we're still checking Firestore, show a spinner or a short message
  if (checkingInstallation) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Google Looker Integration</DialogTitle>
        <DialogContent dividers sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Checking Looker installation status...
          </Typography>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Google Looker Integration</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <img
            src="/assets/looker-icon.png"
            alt="Looker icon"
            style={{ width: 80, marginBottom: 8 }}
          />
        </Box>

        {errorMsg && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMsg}
          </Alert>
        )}

        <Typography variant="body1" sx={{ mb: 2 }}>
          {isInstalled
            ? "Your organization is currently linked to Looker. " +
              "You can update or expand which forms/fields are synced to BigQuery."
            : "Connect your form data to Google Looker for advanced BI and custom dashboards."
          }
        </Typography>

        <Typography variant="body2" color="text.secondary">
          Once complete, you'll be able to map specific form fields
          and choose which data you want available in Looker. You
          can then build dashboards and share insights with your team.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>

        <Button
          onClick={handleInstallClick}
          variant="contained"
          color="primary"
        >
          {isInstalled ? "Update Integration" : "Install Integration"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
